import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  Home, Trophy, MapPin, Vote, Store, User, LogOut, Bell, Shield, Award, Crown,
  Facebook, Music2, AlertTriangle, MoreHorizontal, X, Heart, KeyRound,
  PartyPopper, Megaphone, Sparkles, Landmark, HelpCircle, Info, Tv, Flame,
  Phone, Fuel, Coins, MessageSquare, Gift, ShoppingBag, Activity,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import ReportIssueModal from "@/components/ReportIssueModal";

/* Bottom-mobile-nav "More" sheet — single source of truth for the secondary
 * destination tree. Renders Home-grouped + Discover-grouped sections so a
 * hunter who lands on /home can find missions, the LM portal, private-event
 * hosting, and the splash page from one tap. */
const MORE_GROUPS = [
  {
    title: "Discover",
    items: [
      { to: "/stash",               icon: Coins,        label: "Daily Stash",        testid: "more-stash",        accent: "#D7A62A" },
      { to: "/vault",               icon: KeyRound,     label: "The Daily Vault",    testid: "more-vault",        accent: "#D7A62A" },
      { to: "/bazaar",              icon: ShoppingBag,  label: "The Loot Bazaar",    testid: "more-bazaar",       accent: "#D7A62A" },
      { to: "/auctions",            icon: Trophy,       label: "Loot Auctions",      testid: "more-auctions",     accent: "#76B82A" },
      { to: "/my-prizes",           icon: Gift,         label: "My Prizes",          testid: "more-my-prizes",    accent: "#76B82A" },
      { to: "/holler",              icon: MessageSquare, label: "The Holler",        testid: "more-holler",       accent: "#76B82A" },
      { to: "/pulse",               icon: Activity,     label: "The Pulse · Feedback", testid: "more-pulse",      accent: "#76B82A" },
      { to: "/met-the-founder",     icon: Crown,        label: "Met the Founder",    testid: "more-met-founder",  accent: "#D7A62A" },
      { to: "/spoils",              icon: ShoppingBag,  label: "The Spoils · Store", testid: "more-spoils",       accent: "#D7A62A" },
      { to: "/badges",              icon: Award,        label: "Badge Case",         testid: "more-badges",       accent: "#D7A62A" },
      { to: "/missions",            icon: Heart,        label: "Community missions", testid: "more-missions",     accent: "#76B82A" },
      { to: "/community-tank",      icon: Fuel,         label: "Community Tank",     testid: "more-tank",         accent: "#76B82A" },
      { to: "/lootmaster",          icon: KeyRound,     label: "Loot Master program", testid: "more-lootmaster",   accent: "#D7A62A" },
      { to: "/private/sales",       icon: PartyPopper,  label: "Host a private hunt", testid: "more-private",      accent: "#D7A62A" },
      { to: "/sponsor-a-mission",   icon: Megaphone,    label: "Sponsor a mission",   testid: "more-sponsor-msn",  accent: "#76B82A" },
    ],
  },
  {
    title: "Hunt",
    items: [
      { to: "/drops",       icon: Flame,     label: "Drops",        testid: "more-drops" },
      { to: "/waymarks",    icon: Landmark,  label: "Waymarks",     testid: "more-waymarks" },
      { to: "/loot-quest",  icon: Sparkles,  label: "Loot Quest",   testid: "more-loot-quest" },
      { to: "/golden",      icon: Crown,     label: "Golden drops", testid: "more-golden", accent: "#D7A62A" },
      { to: "/vote",        icon: Vote,      label: "Vote your city", testid: "more-vote" },
      { to: "/sponsors",    icon: Store,     label: "Sponsors",     testid: "more-sponsors" },
    ],
  },
  {
    title: "Info",
    items: [
      { to: "/",             icon: Tv,          label: "About the Loot",  testid: "more-splash" },
      { to: "/how-it-works", icon: HelpCircle,  label: "How it works",    testid: "more-how" },
      { to: "/about",        icon: Info,        label: "Our story",       testid: "more-about" },
      { to: "/contact",      icon: Phone,       label: "Contact",         testid: "more-contact" },
    ],
  },
];

function MoreSheet({ open, onClose, stashVisible = false, vaultVisible = false }) {
  // Lock body scroll while open. Uses class so we don't fight with anyone
  // else's inline style.
  useEffect(() => {
    if (open) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [open]);
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-40" data-testid="more-sheet" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        data-testid="more-sheet-backdrop"
      />
      <div className="absolute bottom-0 inset-x-0 bg-[#0d100d] border-t-2 border-[#76B82A]/40 max-h-[85vh] overflow-y-auto rounded-t-2xl shadow-[0_-12px_30px_-8px_rgba(0,0,0,0.6)]">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-[#F5F2E8]/10 sticky top-0 bg-[#0d100d]">
          <div>
            <div className="overline">Explore</div>
            <h2 className="font-display uppercase text-2xl tracking-tight mt-0.5">More of the loot</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 text-[#F5F2E8]/70 hover:text-[#F5F2E8]"
            data-testid="more-sheet-close"
          >
            <X size={22} />
          </button>
        </div>
        <div className="px-5 pt-4 pb-8 space-y-6">
          {MORE_GROUPS.map((g) => {
            const items = g.items.filter((it) => {
              // Stash + the entire coin economy hide together until Stash flips live.
              if (["/stash", "/bazaar", "/auctions", "/my-prizes"].includes(it.to) && !stashVisible) return false;
              // Vault has its own master toggle.
              if (it.to === "/vault" && !vaultVisible) return false;
              return true;
            });
            if (items.length === 0) return null;
            return (
            <div key={g.title}>
              <div className="overline mb-2">{g.title}</div>
              <div className="grid grid-cols-2 gap-2.5">
                {items.map(({ to, icon: Icon, label, testid, accent }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={onClose}
                    data-testid={testid}
                    className="loot-card p-3 flex flex-col items-start gap-1.5"
                  >
                    <Icon size={18} style={{ color: accent || "#76B82A" }} />
                    <span className="font-display uppercase tracking-tight text-sm leading-tight">{label}</span>
                  </Link>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const refreshUnread = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch {
      setUnread(0);
    }
  }, [user]);

  // Stash visibility — hidden from regular users until admin flips mode=live.
  // Admins always see it (so they can test). We poll every 60s in case the
  // admin toggles mode in another tab.
  const [stashVisible, setStashVisible] = useState(false);
  const [vaultVisible, setVaultVisible] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const [s, v] = await Promise.all([
          api.get("/stash/status").catch(() => ({ data: { visible: false } })),
          api.get("/vault/status").catch(() => ({ data: { visible: false } })),
        ]);
        if (!cancelled) {
          setStashVisible(!!s.data?.visible);
          setVaultVisible(!!v.data?.visible);
        }
      } catch {
        if (!cancelled) { setStashVisible(false); setVaultVisible(false); }
      }
    };
    refresh();
    const t = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    const handler = () => setUnread(0);
    window.addEventListener("loot:notifications-read", handler);
    return () => { clearInterval(t); window.removeEventListener("loot:notifications-read", handler); };
  }, [refreshUnread]);

  const NavLinkItem = ({ to, children, testid }) => (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `px-3 py-2 font-display uppercase tracking-widest text-sm transition ${
          isActive ? "text-[#76B82A]" : "text-[#F5F2E8]/70 hover:text-[#F5F2E8]"
        }`
      }
    >
      {children}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col grain" style={{ position: "relative" }}>
      <header
        className="sticky top-0 z-30 backdrop-blur-xl loot-panel-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/home" data-testid="nav-logo" className="flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="The Local Loot"
              className="h-10 md:h-12 w-auto transition-transform group-hover:scale-105"
              style={{ filter: "drop-shadow(2px 2px 0 rgba(0,0,0,0.6))" }}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLinkItem to="/home" testid="nav-home">Home</NavLinkItem>
            <NavLinkItem to="/winners" testid="nav-winners">Winners</NavLinkItem>
            {stashVisible && (
              <>
                <NavLinkItem to="/stash" testid="nav-stash">Stash</NavLinkItem>
                <NavLinkItem to="/bazaar" testid="nav-bazaar">
                  <span className="inline-flex items-center gap-1"><ShoppingBag size={12} style={{ color: "#D7A62A" }} /> Bazaar</span>
                </NavLinkItem>
              </>
            )}
            {vaultVisible && (
              <NavLinkItem to="/vault" testid="nav-vault">
                <span className="inline-flex items-center gap-1"><KeyRound size={12} style={{ color: "#D7A62A" }} /> Vault</span>
              </NavLinkItem>
            )}
            <NavLinkItem to="/holler" testid="nav-holler">Holler</NavLinkItem>
            <NavLinkItem to="/missions" testid="nav-missions">
              <span className="inline-flex items-center gap-1"><Heart size={12} style={{ color: "#76B82A" }} /> Missions</span>
            </NavLinkItem>
            <NavLinkItem to="/waymarks" testid="nav-waymarks">Waymarks</NavLinkItem>
            <NavLinkItem to="/leaderboard" testid="nav-leaderboard">Ranks</NavLinkItem>
            <NavLinkItem to="/golden" testid="nav-golden">
              <span className="inline-flex items-center gap-1"><Crown size={12} style={{ color: "#D7A62A" }} /> Golden</span>
            </NavLinkItem>
            <NavLinkItem to="/vote" testid="nav-vote">Vote</NavLinkItem>
            <NavLinkItem to="/sponsors" testid="nav-sponsors">Sponsors</NavLinkItem>
            <NavLinkItem to="/private/sales" testid="nav-host">
              <span className="inline-flex items-center gap-1" style={{ color: "#D7A62A" }}>Host</span>
            </NavLinkItem>
            <NavLinkItem to="/lootmaster" testid="nav-lootmaster">
              <span className="inline-flex items-center gap-1"><KeyRound size={12} style={{ color: "#D7A62A" }} /> LM</span>
            </NavLinkItem>
            <NavLinkItem to="/how-it-works" testid="nav-how">How</NavLinkItem>
            <NavLinkItem to="/contact" testid="nav-contact">Contact</NavLinkItem>
            {(user?.role === "admin" || user?.role === "lootmaster") && (
              <NavLinkItem to="/admin" testid="nav-admin">
                {user?.role === "lootmaster" ? "Console" : "Admin"}
              </NavLinkItem>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link
              to="/notifications"
              data-testid="nav-notifications"
              className="relative p-2 text-[#F5F2E8]/70 hover:text-[#76B82A]"
            >
              <Bell size={18} />
              {unread > 0 && (
                <span
                  data-testid="unread-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center font-mono text-[10px] font-bold"
                  style={{ background: "#E53935", color: "#F5F2E8", borderRadius: 999 }}
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            {user ? (
              <>
                <Link
                  to="/profile"
                  data-testid="nav-profile"
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[#F5F2E8]/20 hover:border-[#76B82A]"
                >
                  <User size={14} />
                  <span className="font-mono text-xs uppercase tracking-widest">
                    {user.username}
                  </span>
                </Link>
                <button
                  onClick={async () => { await logout(); navigate("/login"); }}
                  data-testid="nav-logout"
                  className="p-2 text-[#F5F2E8]/70 hover:text-[#E53935]"
                  aria-label="Logout"
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="btn-ghost text-xs">
                Enter
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-8 pb-28 md:pb-12 relative z-10">
        {children}
      </main>

      {/* Footer — socials + motto */}
      <footer
        className="relative z-10 border-t pb-28 md:pb-8 pt-8 loot-panel-footer"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <div className="font-display uppercase text-lg">The Local Loot</div>
            <div
              className="font-mono text-xs mt-1"
              style={{ color: "#76B82A", letterSpacing: "0.06em" }}
              data-testid="footer-motto"
            >
              Live Life · Do Good · Be Happy
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="https://facebook.com/thelocalloot"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The Local Loot on Facebook"
              data-testid="footer-facebook"
              className="btn-ghost text-xs inline-flex items-center gap-2"
            >
              <Facebook size={14} />
              <span>Facebook</span>
            </a>
            <a
              href="https://www.tiktok.com/@thelocalloot"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="The Local Loot on TikTok"
              data-testid="footer-tiktok"
              className="btn-ghost text-xs inline-flex items-center gap-2"
            >
              <Music2 size={14} />
              <span>TikTok</span>
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-5 font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/40 flex items-center justify-between flex-wrap gap-3">
          <span>© {new Date().getFullYear()} The Local Loot · All times shown in ET</span>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              className="hover:text-[#E53935] inline-flex items-center gap-1"
              data-testid="footer-report"
            >
              <AlertTriangle size={11} /> Report issue
            </button>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/terms" className="hover:text-[#D7A62A]" data-testid="footer-terms">Terms</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/privacy" className="hover:text-[#1e88e5]" data-testid="footer-privacy">Privacy</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/safety" className="hover:text-[#E53935]" data-testid="footer-safety">Safety</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/private/sales" className="hover:text-[#D7A62A]" data-testid="footer-host">Host a hunt</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/missions" className="hover:text-[#76B82A]" data-testid="footer-missions">Missions</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/community-tank" className="hover:text-[#76B82A]" data-testid="footer-tank">Community Tank</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/lootmaster" className="hover:text-[#D7A62A]" data-testid="footer-lootmaster">Loot Master</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/" className="hover:text-[#76B82A]" data-testid="footer-splash">About the Loot</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/contact" className="hover:text-[#76B82A]" data-testid="footer-contact">Contact</Link>
            <span className="text-[#F5F2E8]/20">·</span>
            <Link to="/how-it-works" className="hover:text-[#76B82A]">How it works</Link>
          </div>
        </div>
      </footer>

      <ReportIssueModal open={reportOpen} onClose={() => setReportOpen(false)} />

      {/* Mobile bottom nav — 5 slots: Home / Winners / Ranks / More / Profile|Admin.
          The MORE slot opens a slide-up sheet covering the full secondary
          destination tree (missions, LM, private hunts, splash, etc.) so
          hunters never lose access to the new sections from any page. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl loot-panel-mobile-nav"
      >
        <div className="grid grid-cols-5">
          {[
            { kind: "link",  to: "/home",        icon: Home,             label: "Home",     testid: "mnav-home" },
            { kind: "link",  to: "/winners",     icon: Trophy,           label: "Winners",  testid: "mnav-winners" },
            { kind: "link",  to: "/leaderboard", icon: Award,            label: "Ranks",    testid: "mnav-ranks" },
            { kind: "more",                       icon: MoreHorizontal,   label: "More",     testid: "mnav-more" },
            (user?.role === "admin" || user?.role === "lootmaster")
              ? { kind: "link", to: "/admin",   icon: Shield,            label: user?.role === "lootmaster" ? "Console" : "Admin", testid: "mnav-admin" }
              : { kind: "link", to: "/profile", icon: User,              label: "Profile",  testid: "mnav-profile" },
          ].map((item, i) => {
            const { kind, to, icon: Icon, label, testid } = item;
            if (kind === "more") {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  data-testid={testid}
                  className={`flex flex-col items-center justify-center py-2.5 gap-1 transition ${moreOpen ? "text-[#76B82A]" : "text-[#F5F2E8]/60"}`}
                >
                  <Icon size={18} />
                  <span className="font-mono text-[9px] uppercase tracking-widest">{label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2.5 gap-1 transition ${
                    isActive ? "text-[#76B82A]" : "text-[#F5F2E8]/60"
                  }`
                }
              >
                <Icon size={18} />
                <span className="font-mono text-[9px] uppercase tracking-widest">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} stashVisible={stashVisible} vaultVisible={vaultVisible} />
    </div>
  );
}
